import { Dropdown, borderedSelectStyles } from '@/components/Dropdown';
import { Input } from '@/components/Input';
import styles from './Regularization.module.scss';
import { useEffect, useMemo, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { TimePicker } from 'antd';
import moment from 'moment-timezone';
import { regularizationActionTypes } from '@store/Regularization/Regularization.actionTypes';

import { textVariantsEnum, fontWeightEnum, Text } from '@/components/Text';
import {
  getProjectDropdownOptions,
  getTaskDropdownOptions,
  getManagerDropdownOptions,
  getDefaultTimezone,
  calculateDuration,
  validateForm,
  getHoursMinutes,
  getInitialFormData,
  getTeamDropdownOptions,
  check24HoursLimit,
  canUseRegularizationDateSelection,
  isRegularizationDateSelectableInCalendar,
  buildRegularizationFormDateChange,
  managersFromMemberDetails,
} from './Regularization.helper';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { Button, buttonVariants } from '@/components/Button';
import { useTimezones } from '@/hooks/useTimezones';
import { moduleIds, useFeatures } from '@/hooks/useFeatures';
import { useUserTeamsForCompany } from '@/hooks/useUserTeamsForCompany';
import { useTeamMemberDetailsForSelect } from '@hooks/useTeamMemberDetailsForSelect';

/** Ant Design dropdowns default below app modal (z-index 1111); lift time panels above. */
const timePickerPopupProps = {
  getPopupContainer: () => document.body,
  popupStyle: { zIndex: 1200 },
};

export const RegularizationModal = ({
  teams,
  fetchProjectTasks,
  onClose,
  onBusyChange,
}) => {
  const { hasAccess } = useFeatures();
  const hasProjectAccess = hasAccess.module(moduleIds.PROJECTS);
  const hasTaskAccess = hasAccess.module(moduleIds.TASKS);
  const dispatch = useDispatch();
  const { profile } = useSelector((state) => state.auth);
  const { activeCompany } = useSelector((state) => state.auth.profile);
  const { teams: userTeamsForCompany } = useUserTeamsForCompany(
    profile?.id,
    activeCompany?.companyId
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitInFlightRef = useRef(false);
  const [taskLists, setTaskLists] = useState([]);
  const [formData, setFormData] = useState(
    getInitialFormData(getDefaultTimezone())
  );
  /** After user picks a different team, do not auto-select the first manager again. */
  const mayDefaultAssigneeToFirstRef = useRef(true);
  const activeTeamId =
    teams.active?.activeTeamid ?? teams.active?.activeTeamId ?? null;
  const teamIdForManagers = formData.team?.value ?? activeTeamId;
  // Full roster required: scoped `memberDetails` is self-only for team Members, which yields no managers.
  const { memberDetailsUnfiltered } =
    useTeamMemberDetailsForSelect(teamIdForManagers);
  const availableManagers = useMemo(
    () => managersFromMemberDetails(memberDetailsUnfiltered),
    [memberDetailsUnfiltered]
  );
  const setBusy = (busy) => {
    if (typeof onBusyChange === 'function') {
      onBusyChange(busy);
    }
  };
  const projectDropdownOptions = useMemo(() => {
    return getProjectDropdownOptions(teams);
  }, [teams?.projects]);
  const taskDropdownOptions = useMemo(() => {
    return getTaskDropdownOptions(taskLists);
  }, [taskLists]);
  const managerDropdownOptions = useMemo(() => {
    return getManagerDropdownOptions(availableManagers);
  }, [availableManagers]);
  const teamDropdownOptions = useMemo(
    () => getTeamDropdownOptions(userTeamsForCompany),
    [userTeamsForCompany]
  );
  const timezoneDropdownOptions = useTimezones();

  const todayMax = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  useEffect(() => {
    if (!teamDropdownOptions.length) return;
    setFormData((prev) => {
      if (prev.team?.value) return prev;
      const activeId =
        teams.active?.activeTeamid ?? teams.active?.activeTeamId ?? null;
      const match = activeId
        ? teamDropdownOptions.find((o) => o.value === activeId)
        : null;
      const pick = match || teamDropdownOptions[0];
      return {
        ...prev,
        team: pick ? { value: pick.value, label: pick.label } : null,
      };
    });
  }, [
    teamDropdownOptions,
    teams.active?.activeTeamid,
    teams.active?.activeTeamId,
  ]);

  // Keep assignee valid for the roster; default first manager only on first load (before user changes team).
  useEffect(() => {
    if (!teamIdForManagers || !availableManagers.length) return;
    setFormData((prev) => {
      const opts = getManagerDropdownOptions(availableManagers);
      const uid = prev.assignee?.userUID ?? prev.assignee?.value;
      if (uid && opts.some((o) => o.value === uid)) return prev;
      if (!uid && mayDefaultAssigneeToFirstRef.current && opts.length > 0) {
        return { ...prev, assignee: opts[0] };
      }
      return { ...prev, assignee: '' };
    });
  }, [teamIdForManagers, availableManagers]);

  const handleTeamChange = (team) => {
    mayDefaultAssigneeToFirstRef.current = false;
    setFormData((prev) => ({
      ...prev,
      team,
      assignee: '',
    }));
  };

  const handleProjectChange = (project) => {
    setFormData((prev) => ({
      ...prev,
      projectId: project,
      projectName: project.label,
      taskId: null,
      taskName: '',
    }));
    fetchProjectTasks(project.id, (fetchedTasks) => setTaskLists(fetchedTasks));
  };
  const handleTaskChange = (task) => {
    setFormData((prev) => ({
      ...prev,
      taskId: task,
      taskName: task.label,
    }));
  };
  const handleAssigneeChange = (assignee) => {
    setFormData((prev) => ({
      ...prev,
      assignee: assignee,
    }));
  };
  const handleDateChange = (date) => {
    if (!date) {
      setFormData((prev) => ({ ...prev, date: null }));
      return;
    }
    if (!canUseRegularizationDateSelection(date)) return;
    setFormData((prev) => buildRegularizationFormDateChange(prev, date));
  };
  const handleTimezoneChange = (timezone) => {
    setFormData((prev) => ({
      ...prev,
      timezone: timezone,
    }));
  };
  const handleStartTimeChange = (startTime) => {
    setFormData((prev) => ({
      ...prev,
      startTime: startTime,
    }));
  };
  const handleEndTimeChange = (endTime) => {
    setFormData((prev) => ({
      ...prev,
      endTime: endTime,
    }));
  };
  const duration = useMemo(() => {
    const duration = calculateDuration(formData.startTime, formData.endTime);
    const durationText = duration.isValid
      ? `${String(duration.hours).padStart(2, '0')}h ${String(
          duration.minutes
        ).padStart(2, '0')}m`
      : '00h 00m';
    return { isValid: duration.isValid, durationText };
  }, [formData.startTime, formData.endTime]);

  const handleSubmit = async () => {
    if (submitInFlightRef.current || isSubmitting) return;
    submitInFlightRef.current = true;
    setIsSubmitting(true);
    setBusy(true);
    const isValid = validateForm(formData);
    if (!isValid) {
      submitInFlightRef.current = false;
      setIsSubmitting(false);
      setBusy(false);
      return;
    }
    const timeLimitCheck = await check24HoursLimit(formData, teams, profile);
    if (!timeLimitCheck) {
      submitInFlightRef.current = false;
      setIsSubmitting(false);
      setBusy(false);
      return;
    }
    const duration = calculateDuration(formData.startTime, formData.endTime);
    const dateStr = formData.date
      ? moment(formData.date).format('YYYY-MM-DD')
      : null;
    const { hours: startHours, minutes: startMinutes } = getHoursMinutes(
      formData.startTime
    );
    const { hours: endHours, minutes: endMinutes } = getHoursMinutes(
      formData.endTime
    );

    const requestData = {
      userId: profile.id,
      userName:
        (profile?.name && String(profile.name).trim()) || profile?.email || '',
      companyID: activeCompany.companyId,
      projectId: formData.projectId?.id ?? null,
      taskName: formData.taskName || '',
      taskID: formData.taskId?.id ?? null,
      taskDetails: formData.taskDetails || '',
      assignee: formData.assignee?.userUID,
      taskHours: duration.hours,
      taskMinutes: duration.minutes,
      status: 'Pending',
      typeLog: 'Manual',
      startTime: dateStr,
      requestDate: new Date(),
      endTime: { endHours, endMinutes },
      startHours,
      startMinutes,
      timezone: formData.timezone?.value || getDefaultTimezone(),
      teamId: formData.team?.value ?? null,
      teamName: formData.team?.label ?? null,
    };

    const result = await new Promise((resolve) => {
      dispatch({
        type: regularizationActionTypes.CREATE_REQUEST,
        payload: {
          requestData,
          onComplete: resolve,
        },
      });
    });
    if (result?.success) {
      onClose();
    }
    submitInFlightRef.current = false;
    setIsSubmitting(false);
    setBusy(false);
  };
  return (
    <div className={styles.regularizationModal}>
      <Dropdown
        options={teamDropdownOptions}
        value={formData.team}
        label="Department *"
        selectStyles={borderedSelectStyles}
        onChange={handleTeamChange}
        placeholder="Select Department"
      />
      {hasProjectAccess && (
        <Dropdown
          options={projectDropdownOptions}
          value={formData.projectId}
          label="Project"
          selectStyles={borderedSelectStyles}
          onChange={handleProjectChange}
          placeholder="Select Project"
        />
      )}
      {hasTaskAccess && (
        <Dropdown
          options={taskDropdownOptions}
          value={formData.taskId}
          label="Task"
          selectStyles={borderedSelectStyles}
          onChange={handleTaskChange}
          placeholder="Select Task"
        />
      )}
      <Input
        type="text"
        value={formData.taskDetails}
        label="Task Details"
        placeholder="Enter Task Details"
        className={styles.taskDetailsInput}
        onChange={(e) => {
          setFormData((prev) => ({
            ...prev,
            taskDetails: e.target.value,
          }));
        }}
      />
      <Dropdown
        options={managerDropdownOptions}
        value={formData.assignee}
        label="Manager *"
        selectStyles={borderedSelectStyles}
        onChange={handleAssigneeChange}
        placeholder="Select Manager"
      />
      <div className={styles.datePicker}>
        <Text
          variant={textVariantsEnum.Text_13}
          fontWeight={fontWeightEnum.Normal}
        >
          Date *
        </Text>
        <DatePicker
          className={styles.datePickerInput}
          selected={formData.date}
          onChange={handleDateChange}
          placeholderText="Select date"
          dateFormat="yyyy-MM-dd"
          maxDate={todayMax}
          filterDate={isRegularizationDateSelectableInCalendar}
          popperClassName={styles.regularizationDatePickerPopper}
        />
      </div>
      <Dropdown
        options={timezoneDropdownOptions}
        value={formData.timezone}
        label="Timezone"
        selectStyles={borderedSelectStyles}
        onChange={handleTimezoneChange}
        placeholder="Select Timezone"
      />
      <div className={styles.timePicker}>
        <Text
          variant={textVariantsEnum.Text_13}
          fontWeight={fontWeightEnum.Normal}
        >
          Task Start Time *
        </Text>
        <TimePicker
          use12Hours
          showSecond={false}
          value={formData.startTime}
          className={styles.timePickerInput}
          onChange={handleStartTimeChange}
          placeholder="Select Task Start Time"
          {...timePickerPopupProps}
        />
      </div>
      <div className={styles.timePicker}>
        <Text
          variant={textVariantsEnum.Text_13}
          fontWeight={fontWeightEnum.Normal}
        >
          Task End Time *
        </Text>
        <TimePicker
          use12Hours
          showSecond={false}
          value={formData.endTime}
          className={styles.timePickerInput}
          onChange={handleEndTimeChange}
          placeholder="Select Task End Time"
          {...timePickerPopupProps}
        />
      </div>
      <div
        className={classNames(styles.duration, {
          [styles.duration_valid]: duration.isValid,
        })}
      >
        <Text
          variant={textVariantsEnum.Text_13}
          fontWeight={fontWeightEnum.Normal}
        >
          Duration
        </Text>
        <Input value={duration.durationText} disabled />
      </div>
      <Button
        text="Submit"
        onClick={handleSubmit}
        variant={buttonVariants.primary}
        loading={isSubmitting}
      />
    </div>
  );
};
